import { Box, Container, Heading, Text, VStack, Grid } from "@chakra-ui/react";
import { Helmet } from 'react-helmet-async';
import ServiceDetailsHeader from "../../components/Common/ImageHeader";
import CoCreationSection from "../../components/Common/CoCreationSection";
import ExploreSection from "../../components/ProductDetails/ExploreSection";

const TechSupportServicesPage = () => {
  // Modern Tools & Technologies Data
  const modernToolsData = [
    {
      id: 1,
      icon: "/svg/Layer_1.svg",
      title:
        "Service desk platforms (e.g., Freshdesk, Zoho Desk, custom-built tools)",
      description: "",
    },
    {
      id: 2,
      icon: "/svg/Layer_2.svg",
      title: "Real-time monitoring tools (e.g., Nagios, Zabbix, UptimeRobot)",
      description: "",
    },
    {
      id: 3,
      icon: "/svg/Layer_3.svg",
      title: "Cloud telephony & IVR systems",
      description: "",
    },
    {
      id: 4,
      icon: "/svg/Layer_4.svg",
      title:
        "Chatbots and AI assistants for level 1 responses (on select platforms)",
      description: "",
    },
    {
      id: 5,
      icon: "/svg/Layer_5.svg",
      title:
        "Analytics dashboards to track SLAs, uptime, and user satisfaction",
      description: "",
    },
  ];

  // Tech Support Services Data
  const techSupportServices = [
    {
      id: 1,
      title: "Helpline Support",
      description: "We operate dedicated user-facing support lines to manage:",
      features: [
        "General inquiries and usage assistance",
        "Complaint registration and tracking",
        "Basic troubleshooting and escalation",
      ],
    },
    {
      id: 2,
      title: "System Monitoring & Proactive Maintenance",
      description: "We ensure performance through:",
      features: [
        "24/7 real-time monitoring of server health, application status, and network performance",
        "Automated alerts and performance analytics",
        "Security patching and preventive updates",
      ],
    },
    {
      id: 3,
      title: "Remote Help Desk (Ticket-Based)",
      description: "Centralized issue resolution via:",
      features: [
        "Online ticketing portal, email, or phone",
        "Remote diagnosis and bug resolution",
        "SLA-compliant response and escalation",
      ],
    },
    {
      id: 4,
      title: "On-Site Technical Support",
      description: "For critical deployments, we provide:",
      features: [
        "On-premise diagnostics, upgrades, or maintenance",
        "Emergency response and configuration updates",
        "Support for hardware-software integration",
      ],
    },
    {
      id: 5,
      title: "Training & Knowledge Transfer",
      description: "We don't just support systems—we support people:",
      features: [
        "Customized training for end users and system admins",
        "Train-the-Trainer (ToT) programs for sustainability",
        "Manuals, quick-start guides, and video tutorials",
      ],
    },
    {
      id: 6,
      title: "Crisis & Incident Management",
      description: "We are prepared for emergencies with:",
      features: [
        "Structured escalation protocols",
        "Data recovery and system restoration",
        "Root cause analysis and post-incident reporting",
      ],
    },
  ];

  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>Tech Support Services | 24/7 Helpdesk, Remote Monitoring & SLA-Based IT Support in Bangladesh</title>
        <meta 
          name="description" 
          content="Explore Business Automation's end-to-end tech support services—offering 24/7 helpdesk, on-site technical support, proactive monitoring, and crisis management for mission-critical government and enterprise platforms." 
        />
        <meta 
          name="keywords" 
          content="IT support services Bangladesh, government tech support Bangladesh, 24/7 helpdesk services Bangladesh, remote tech support for e-Government, on-site technical support Bangladesh, crisis management support Bangladesh, SLA-based support Bangladesh, training and knowledge transfer IT Bangladesh, proactive system monitoring Bangladesh, customer support digital systems" 
        />
        <link rel="canonical" href="https://www.ba-systems.com/services/tech-support-services" />
        <meta name="robots" content="index, follow" />

        {/* Open Graph / Facebook */}
        <meta property="og:url" content="https://ba-systems.com/services/tech-support-services" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Tech Support Services | 24/7 Helpdesk, Remote Monitoring & SLA-Based IT Support in Bangladesh" />
        <meta property="og:description" content="Explore Business Automation's end-to-end tech support services—offering 24/7 helpdesk, on-site technical support, proactive monitoring, and crisis management for mission-critical government and enterprise platforms." />
        <meta property="og:image" content="https://opengraph.b-cdn.net/production/images/e1930f8a-3aa2-4fe1-b6ea-0ad5030ebc97.png?token=vTkCXbq7qBnee_uxOFTj3-OKo7vP2KVzX66L6BYKzho&height=596&width=1200&expires=33297038875" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content="ba-systems.com" />
        <meta property="twitter:url" content="https://ba-systems.com/services/tech-support-services" />
        <meta name="twitter:title" content="Tech Support Services | 24/7 Helpdesk, Remote Monitoring & SLA-Based IT Support in Bangladesh" />
        <meta name="twitter:description" content="Explore Business Automation's end-to-end tech support services—offering 24/7 helpdesk, on-site technical support, proactive monitoring, and crisis management for mission-critical government and enterprise platforms." />
        <meta name="twitter:image" content="https://opengraph.b-cdn.net/production/images/e1930f8a-3aa2-4fe1-b6ea-0ad5030ebc97.png?token=vTkCXbq7qBnee_uxOFTj3-OKo7vP2KVzX66L6BYKzho&height=596&width=1200&expires=33297038875" />
      </Helmet>
      
      <ServiceDetailsHeader
        title="Tech Support Services"
        subtitle="24/7 Technical Support & Maintenance Solutions"
        backgroundImage="/png/bg_tech.png"
        breadcrumbItems={[
          { label: "Home", href: "/" },
          { label: "Services", href: "/our-services" },
          { label: "Tech Support Services", href: "#", isCurrentPage: true },
        ]}
      />

      <Box
        w="full"
        backgroundImage="url('/png/background.png')"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundRepeat="no-repeat"
      >
        {" "}
        <Box py={{ base: "30px", md: "40px" }}>
          <Container maxW="7xl">
            <VStack spacing={8} align="start">
              <VStack spacing={4} align="start" w="full">
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="gray.600"
                  lineHeight="1.2"
                  textAlign="justify"
                >
                  At Business Automation Limited, we believe that technology alone
                  isn’t enough—what truly delivers value is long-term support,
                  timely intervention, and system continuity. That’s why our
                  Tech Support Services are designed to keep mission-critical
                  platforms running smoothly for governments, enterprises, and
                  development partners.
                </Text>
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="gray.600"
                  lineHeight="1.2"
                  textAlign="justify"
                >
                  From large-scale e-Government platforms to enterprise
                  solutions, we offer comprehensive technical and operational
                  support through multi-channel helpdesks, proactive monitoring,
                  on-site teams, and capacity-building programs.
                </Text>
              </VStack>
              <Heading
                fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                fontWeight="bold"
                color="gray.800"
              >
                Why{" "}
                <Text as="span" color="#000B8C">
                  our Tech support matters
                </Text>
              </Heading>
              <Box w="full">
                <img
                  src="/png/pyramid.png"
                  alt="Application Migration Services"
                  style={{
                    width: "100%",
                    height: "auto",
                    borderRadius: "12px",
                    objectFit: "cover",
                  }}
                  loading="lazy"
                />
              </Box>

              {/* Comprehensive Tech Support Services Section */}
              <Box w="full" py={16}>
                <VStack spacing={12} align="start" w="full">
                  <Heading
                    fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                    fontWeight="bold"
                    color="gray.800"
                    textAlign="center"
                    w="full"
                  >
                    Comprehensive{" "}
                    <Text as="span" color="#000B8C">
                      Tech support services
                    </Text>
                  </Heading>

                  {/* Services Grid - 2 rows, 3 columns */}
                  <Grid
                    templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
                    gap={6}
                    w="full"
                    maxW="7xl"
                    mx="auto"
                  >
                    {techSupportServices.map((service) => (
                      <Box
                        key={service.id}
                        p={6}
                        bg="white"
                        borderRadius="12px"
                        border="1px solid"
                        borderColor="gray.200"
                        _hover={{ shadow: "lg", transform: "translateY(-2px)" }}
                        transition="all 0.3s ease"
                      >
                        <VStack align="start" spacing={4} h="full">
                          <Heading
                            size="md"
                            color="#000B8C"
                            fontWeight="normal"
                            fontSize="lg"
                          >
                            {service.title}
                          </Heading>
                          <Text color="gray.600" fontSize="sm" lineHeight="1.6">
                            {service.description}
                          </Text>
                          <VStack align="start" spacing={1} pl={2}>
                            {service.features.map((feature, index) => (
                              <Text key={index} color="gray.600" fontSize="sm">
                                • {feature}
                              </Text>
                            ))}
                          </VStack>
                        </VStack>
                      </Box>
                    ))}
                  </Grid>
                </VStack>
              </Box>

              {/* Modern Tools & Technologies Section */}
              <CoCreationSection
              blackTitle={"Tools & Technologies"}
              blueTitle={"Enabled by Modern "}
                // title="Enabled by Modern Tools & Technologies"
                subtitle="We utilize a smart support ecosystem powered by"
                processes={modernToolsData}
                illustrationImage="/png/modern_tech.png"
                bgColor="white"
              />
            </VStack>
          </Container>
        </Box>
        <ExploreSection />
      </Box>
    </>
  );
};

export default TechSupportServicesPage;
